import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Upload, X, Loader2, Image as ImageIcon, Link2 } from 'lucide-react';

export default function GalleryManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    url: '',
    title: '',
    subtitle: '',
    category: '',
    location: '',
    link: '',
    order: 0,
    active: true,
    source: 'dashboard'
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const res = await api.get('/gallery');
      setItems(res.data || []);
    } catch (e) {
      console.error('Failed to fetch gallery items:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData({ url: '', title: '', subtitle: '', category: '', location: '', link: '', order: items.length, active: true, source: 'dashboard' });
    setShowDialog(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      url: item.url || '',
      title: item.title || '',
      subtitle: item.subtitle || '',
      category: item.category || '',
      location: item.location || '',
      link: item.link || '',
      order: item.order ?? 0,
      active: item.active !== false,
      source: item.source || 'dashboard'
    });
    setShowDialog(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await api.post('/upload', body);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setFormData((prev) => ({ ...prev, url: `${backendUrl}${res.data.url}` }));
    } catch (err) {
      console.error('Failed to upload image:', err);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/gallery/${editingId}`, formData);
      } else {
        await api.post('/gallery', formData);
      }
      setShowDialog(false);
      fetchItems();
    } catch (err) {
      console.error('Failed to save item:', err);
      alert('Failed to save item');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this gallery item?')) return;
    try {
      await api.delete(`/gallery/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      console.error('Failed to delete item:', err);
      alert('Failed to delete item');
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gallery</h1>
          <p className="text-muted-foreground">Manage images that appear on the public gallery</p>
        </div>
        <Button onClick={handleCreate} data-testid="add-gallery-item-btn">
          <Plus className="mr-2 h-4 w-4" /> Add Item
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">Loading gallery...</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <div className="text-lg mb-2">No items yet</div>
            <div className="text-gray-500 mb-4">Start by adding your first gallery image</div>
            <Button onClick={handleCreate}><Plus className="mr-2 h-4 w-4" /> Add Item</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.sort((a,b) => (a.order||0) - (b.order||0)).map((item) => (
            <Card key={item.id}>
              <div className="relative aspect-[4/3] bg-gray-100">
                {item.url ? (
                  <img src={item.url} alt={item.title || 'Gallery image'} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                )}
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${item.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {item.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <CardHeader>
                <CardTitle className="text-base">{item.title || 'Untitled'}</CardTitle>
                <div className="text-xs text-gray-500">Order: {item.order ?? 0}</div>
                {(item.category || item.location) && (
                  <div className="text-xs text-gray-500 mt-1">
                    {[item.category, item.location].filter(Boolean).join(' • ')}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(item)}>
                    <Edit className="mr-2 h-3 w-3" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 text-red-600 hover:text-red-700" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="mr-2 h-3 w-3" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Gallery Item' : 'Add Gallery Item'}</DialogTitle>
            <DialogDescription>Upload an image and add optional metadata</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-2">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Image *</Label>
                {formData.url ? (
                  <div className="relative inline-block w-full">
                    <img src={formData.url} alt="Gallery" className="w-full h-48 object-cover rounded-lg" />
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, url: '' }))} className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div className="text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="gallery-image-upload" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">Upload image</span>
                          <input id="gallery-image-upload" type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} />
                        </label>
                        <p className="mt-1 text-xs text-gray-500">PNG, JPG up to 10MB</p>
                      </div>
                      {uploading && (
                        <div className="mt-3 flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">Uploading...</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input id="title" value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} />
              </div>

              {/* Subtitle */}
              <div className="space-y-2">
                <Label htmlFor="subtitle">Subtitle</Label>
                <Input id="subtitle" value={formData.subtitle} onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))} />
              </div>

              {/* Category & Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Input id="category" value={formData.category} onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input id="location" value={formData.location} onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))} />
                </div>
              </div>

              {/* Link */}
              <div className="space-y-2">
                <Label htmlFor="link">Link (e.g., /property/:id)</Label>
                <div className="flex gap-2">
                  <Link2 className="h-4 w-4 mt-3 text-gray-500" />
                  <Input id="link" value={formData.link} onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))} placeholder="/property/123" />
                </div>
              </div>

              {/* Order & Active */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="order">Display Order</Label>
                  <Input id="order" type="number" value={formData.order} onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value || '0', 10) }))} />
                </div>
                <div className="flex items-center gap-2 mt-6">
                  <Switch id="active" checked={formData.active} onCheckedChange={(v) => setFormData(prev => ({ ...prev, active: v }))} />
                  <Label htmlFor="active">Active</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={!formData.url}>{editingId ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
